import { useCallback, useState, useRef, useEffect } from 'react';
import { Box } from '../../../components/ui';
import { ARTICLE_CATEGORIES, type ArticleCategory } from '../../../types/knowledgeBase';
import './CategoryChips.css';

interface CategoryChipsProps {
  selectedCategories: ArticleCategory[];
  onChange: (categories: ArticleCategory[]) => void;
}

// Categories to show initially (most common)
const PRIMARY_CATEGORIES: ArticleCategory[] = [
  'Jabber',
  'Teams',
  'VPN',
  'Office 365',
  'Password Management',
  'ServiceNow',
];

export function CategoryChips({ selectedCategories, onChange }: CategoryChipsProps) {
  const [showMore, setShowMore] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Get remaining categories for "More" dropdown
  const moreCategories = ARTICLE_CATEGORIES.filter(
    (cat) => !PRIMARY_CATEGORIES.includes(cat)
  );

  const handleToggleCategory = useCallback(
    (category: ArticleCategory) => {
      if (selectedCategories.includes(category)) {
        onChange(selectedCategories.filter((c) => c !== category));
      } else {
        onChange([...selectedCategories, category]);
      }
    },
    [selectedCategories, onChange]
  );

  const handleClearAll = useCallback(() => {
    onChange([]);
  }, [onChange]);

  const handleToggleMore = useCallback(() => {
    setShowMore((prev) => !prev);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMore(false);
      }
    }

    if (showMore) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMore]);

  const isAllSelected = selectedCategories.length === 0;
  const hasMoreSelected = selectedCategories.some((c) => moreCategories.includes(c));

  return (
    <Box className="category-chips">
      <span className="category-chips__label">Categories:</span>

      <button
        className={`category-chips__chip ${isAllSelected ? 'category-chips__chip--active' : ''}`}
        onClick={handleClearAll}
        type="button"
      >
        All
      </button>

      {PRIMARY_CATEGORIES.map((category) => (
        <button
          key={category}
          className={`category-chips__chip ${
            selectedCategories.includes(category) ? 'category-chips__chip--active' : ''
          }`}
          onClick={() => handleToggleCategory(category)}
          type="button"
        >
          {category}
        </button>
      ))}

      <Box className="category-chips__more-wrapper" ref={menuRef}>
        <button
          className={`category-chips__chip category-chips__chip--more ${
            hasMoreSelected ? 'category-chips__chip--active' : ''
          }`}
          onClick={handleToggleMore}
          type="button"
          aria-expanded={showMore}
          aria-haspopup="listbox"
        >
          More
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={`category-chips__more-icon ${showMore ? 'category-chips__more-icon--open' : ''}`}
          >
            <path
              d="M3 4.5l3 3 3-3"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {showMore && (
          <Box className="category-chips__dropdown" role="listbox">
            {moreCategories.map((category) => (
              <button
                key={category}
                className={`category-chips__dropdown-item ${
                  selectedCategories.includes(category)
                    ? 'category-chips__dropdown-item--selected'
                    : ''
                }`}
                onClick={() => handleToggleCategory(category)}
                type="button"
                role="option"
                aria-selected={selectedCategories.includes(category)}
              >
                <span className="category-chips__dropdown-check">
                  {selectedCategories.includes(category) && (
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3 7l3 3 5-5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </span>
                {category}
              </button>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
